import { motion } from 'framer-motion';
import { Layers, Ruler, Timer, ArrowDown, ShieldCheck, AlertTriangle, Info } from 'lucide-react';
import type { LamellaSettlementCalc } from '@/types/calculator';

interface LamellaSettlementProps {
  settlement: LamellaSettlementCalc | undefined;
}

export default function LamellaSettlement({ settlement }: LamellaSettlementProps) {
  if (!settlement) return null;

  const complianceColor =
    settlement.compliance === 'Pass' ? 'text-emerald-400' :
    settlement.compliance === 'Marginal' ? 'text-rb-brand' : 'text-red-400';
  const complianceBg =
    settlement.compliance === 'Pass' ? 'bg-emerald-500/10 border-emerald-500/20' :
    settlement.compliance === 'Marginal' ? 'bg-rb-brand/10 border-rb-brand/20' : 'bg-red-500/10 border-red-500/20';

  return (
    <div className="rb-card p-5 space-y-5">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center">
          <Layers className="w-5 h-5 text-cyan-400" />
        </div>
        <div className="flex-1">
          <h2 className="text-sm font-semibold text-white">Lamella Clarifier — Settlement Design</h2>
          <p className="text-xs text-slate-500">Inclined plate settlement area calculation & hydraulic verification</p>
        </div>
        <div className={`px-3 py-1.5 rounded-lg border text-xs font-bold uppercase tracking-wider ${complianceBg} ${complianceColor}`}>
          {settlement.compliance}
        </div>
      </div>

      {/* KPI Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { icon: Ruler, label: 'Req. Settling Area', value: `${settlement.requiredSettlingAreaM2} m²`, color: 'text-cyan-400' },
          { icon: Layers, label: 'Projected Area', value: `${settlement.projectedSettlingAreaM2} m²`, color: 'text-rb-brand' },
          { icon: ArrowDown, label: 'Surface Loading', value: `${settlement.surfaceLoadingRateMPerHr} m/hr`, color: 'text-slate-300' },
          { icon: Timer, label: 'HRT', value: `${settlement.hydraulicRetentionTimeMin} min`, color: 'text-emerald-400' },
        ].map((kpi, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.05 }}
            className="p-3 rounded-xl border border-slate-800/50 bg-slate-800/30 text-center"
          >
            <kpi.icon className={`w-4 h-4 mx-auto mb-1 ${kpi.color}`} />
            <div className="text-[10px] text-slate-500 uppercase tracking-wider font-bold">{kpi.label}</div>
            <div className={`text-lg font-bold mt-1 ${kpi.color}`}>{kpi.value}</div>
          </motion.div>
        ))}
      </div>

      {/* Detail Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        {[
          { label: 'Flow Rate', value: `${settlement.flowRateM3PerDay} m³/d` },
          { label: 'Flow Rate', value: `${settlement.flowRateM3PerHour} m³/hr` },
          { label: 'Actual Tank Area', value: `${settlement.actualTankAreaM2} m²` },
          { label: 'Plate Count', value: `${settlement.plateCount}` },
          { label: 'Plate Spacing', value: `${settlement.plateSpacingMm} mm` },
          { label: 'Plate Angle', value: `${settlement.plateAngleDegrees}°` },
          { label: 'Est. TSS Removal', value: `${settlement.estimatedTSSRemovalPercent}%` },
          { label: 'Est. BOD Removal', value: `${settlement.estimatedBODRemovalPercent}%` },
        ].map((item, i) => (
          <div key={i} className="flex items-center justify-between p-2.5 rounded-lg bg-slate-800/40 border border-slate-800/40">
            <span className="text-[10px] text-slate-500 uppercase font-bold">{item.label}</span>
            <span className="text-sm font-semibold text-white">{item.value}</span>
          </div>
        ))}
      </div>

      {/* Notes */}
      <div className={`flex items-start gap-2 p-3 rounded-lg border ${complianceBg}`}>
        {settlement.compliance === 'Pass' ? (
          <ShieldCheck className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
        ) : (
          <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
        )}
        <p className="text-xs text-slate-400 leading-relaxed">{settlement.notes}</p>
      </div>

      {/* Design Formula Reference */}
      <div className="p-3 rounded-lg bg-slate-800/30 border border-slate-800/40">
        <div className="flex items-center gap-1.5 mb-1.5">
          <Info className="w-3.5 h-3.5 text-slate-500" />
          <span className="text-[10px] text-slate-500 uppercase tracking-wider font-bold">Design Basis</span>
        </div>
        <p className="text-[11px] text-slate-500 leading-relaxed">
          Settlement area calculated from surface loading rate: <em className="text-slate-400">A = Q / vₗ</em> where <em className="text-slate-400">Q</em> is flow (m³/hr) and <em className="text-slate-400">vₗ</em> is max surface loading (m/hr). Projected area = <em className="text-slate-400">A × n × cos(θ)</em> where <em className="text-slate-400">n</em> is plate count and <em className="text-slate-400">θ</em> is plate angle. Plate spacing typically 50–80 mm at 55–60° for self-cleaning.
        </p>
      </div>
    </div>
  );
}
